import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    CUSTOM_ELEMENTS_SCHEMA,
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    ElementRef,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    contentChildren,
    inject,
    viewChild
} from "@angular/core";
import Basemap from "@arcgis/core/Basemap";
import Color from "@arcgis/core/Color";
import Graphic from "@arcgis/core/Graphic";
import CoreMap from "@arcgis/core/Map";
import esriConfig from "@arcgis/core/config";
import Collection from "@arcgis/core/core/Collection";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import type { ScreenPoint } from "@arcgis/core/core/types";
import * as urlUtils from "@arcgis/core/core/urlUtils";
import Point from "@arcgis/core/geometry/Point";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Layer from "@arcgis/core/layers/Layer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import MediaLayer from "@arcgis/core/layers/MediaLayer.js";
import TileLayer from "@arcgis/core/layers/TileLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import ExtentAndRotationGeoreference from "@arcgis/core/layers/support/ExtentAndRotationGeoreference.js";
import ImageElement from "@arcgis/core/layers/support/ImageElement.js";
import Sublayer from "@arcgis/core/layers/support/Sublayer";
import WMSSublayer from "@arcgis/core/layers/support/WMSSublayer";
import type EsriSymbol from "@arcgis/core/symbols/Symbol";
import MapView from "@arcgis/core/views/MapView";
import Draw from "@arcgis/core/views/draw/Draw";
import LayerView from "@arcgis/core/views/layers/LayerView";
import type { GoToOptions2D } from "@arcgis/core/views/types";
import CoordinateVM from "@arcgis/core/widgets/CoordinateConversion/CoordinateConversionViewModel.js";
import "@arcgis/map-components/components/arcgis-basemap-toggle";
import "@arcgis/map-components/components/arcgis-map";
import "@arcgis/map-components/components/arcgis-scale-bar";
import "@arcgis/map-components/components/arcgis-sketch";
import "@arcgis/map-components/components/arcgis-zoom";
import { MsalService } from "@azure/msal-angular";
import { AuthenticationResult } from "@azure/msal-browser";
import { NgbTooltipConfig } from "@ng-bootstrap/ng-bootstrap";
import _, { differenceWith, remove } from "lodash";
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    firstValueFrom,
    from
} from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { environment } from "~environments/environment";
import { HeaderComponent } from "../header/header.component";
import { MapBaseSubComponent } from "./components/menu/map-base-sub-component/map-base-sub.component";
import { MapFastighetService } from "./components/menu/map-fastighet/map-fastighet.service";
import { MapMenuButtonComponent } from "./components/menu/map-menu-button/map-menu-button.component";
import { GeometryInformation } from "./components/widgets/edit-menu/models/edit-models";
import { LayerErrorMessageComponent } from "./components/widgets/layer-error-message/layer-error-message.component";
import { IDefaults } from "./components/widgets/layer-list/models/perisist-default.model";
import { PersistService } from "./components/widgets/layer-list/service/persist.service";
import { MapPopupComponent } from "./components/widgets/map-popup/map-popup.component";
import { MapPopupService } from "./components/widgets/map-popup/map-popup.service";
import { NavigateComponent } from "./components/widgets/navigate/navigate.component";
import { RightClickPopupComponent } from "./components/widgets/right-click-popup/right-click-popup.component";
import { preloadedMetriaLODs } from "./map-configuration";
import {
    AllLayersReadyEvent,
    LayerReadyEvent,
    MapClickedEvent,
    MapClosingPopupEvent,
    MapHoverEvent,
    MapReadyEvent
} from "./map.events";
import {
    ArcGisClickEvent,
    ArcGisPointerMoveEvent,
    CoordsViewModel,
    IArcGisPointerEvent,
    IHighlightedGraphic,
    IHitItem,
    IMapComponent,
    IMapMouse,
    IMapSketch,
    LayerType,
    MapExtent,
    MapMouseData,
    RightClickData
} from "./map.models";
import {
    IFilterParams,
    IMapDrawLayer,
    IMapGeometry,
    IMapLayer,
    IMapMediaLayerImage,
    IMapSettings,
    IRenderParams,
    IWidgetMenuButton
} from "./map.settings";
import { MapSketch } from "./map.sketch";
import { MapTexts } from "./map.texts";
import { LayerMapperService } from "./services/layer.mapper.service";

import { MapFreehandService } from "./services/map-freehand.service";
import { MapQueryService } from "./services/map-query.service";
import { MapSelectService } from "./services/map-select.service";
import { MapSketch2Service } from "./services/map-sketch2.service";
import { MapUtilsService } from "./services/map-utils.service";
import { SpatialService } from "./services/spatial.service";
import { SymbolService } from "./services/symbol.service";

@Component({
    selector: "rask-map",
    templateUrl: "./map.component.html",
    styleUrls: ["./map.component.scss"],
    providers: [
        NgbTooltipConfig,
        MapFreehandService,
        MapQueryService,
        MapSelectService,
        MapFastighetService,
        MapPopupService
    ],
    imports: [
        HeaderComponent,
        LayerErrorMessageComponent,
        MapMenuButtonComponent,
        NavigateComponent,
        MapPopupComponent,
        AsyncPipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MapComponent implements OnInit, OnDestroy, IMapComponent, AfterViewInit {

    allLayersLoaded: boolean;
    draw: Draw;
    featureLayers: Layer[] = [];
    @Input() filter: Subject<IFilterParams>;
    focusLayers: string[] = [];
    graphicsLayers: GraphicsLayer[] = [];
    layerCreationErrorMessageSource = new BehaviorSubject<string[]>([]);
    layerCreationErrorMessage$ = this.layerCreationErrorMessageSource.asObservable();
    mapClasses: string[] = [];
    mapsettings: IMapSettings;
    mediaLayers: MediaLayer[] = [];
    @Input() refresh: Subject<IRenderParams>;
    sketch: IMapSketch;
    symbols: EsriSymbol[] = [];
    texts = MapTexts.texts;
    view: MapView;
    esriHandlers: ResourceHandle[] = [];
    widgetMenuButtons: IWidgetMenuButton[] = [];

    protected coordsViewModel = new CoordsViewModel();
    protected readonly extraWidgetsPosition = "bottom-right";

    private readonly widgetContainer = viewChild<ElementRef>("widgetContainer");
    private readonly arcgisMap = viewChild<ElementRef>("arcgisMap");
    private readonly coordsWidget = viewChild<ElementRef>("coordsWidget");
    private readonly basemapToggle = viewChild<ElementRef>("basemapToggle");
    private readonly mapNavigateComponent = viewChild<NavigateComponent>(NavigateComponent);
    private readonly mapPopupComponent = viewChild<MapPopupComponent>(MapPopupComponent);
    private readonly mapSubComponents = contentChildren<MapBaseSubComponent>(MapBaseSubComponent);

    private readonly arcgisGeometries = new Map<string, Graphic[]>();
    private readonly authenticationService = inject(AuthenticationService);
    private readonly defaultExtent = new MapExtent(-245473.87255251745, 5316645.306885285, 1581213.1141547896, 7850300.374195421);
    private readonly eventService = inject(EventService);
    private readonly highlight: IHighlightedGraphic[] = [];
    private readonly lastHoverObjects: IHitItem[] = [];
    private readonly layerMapperService = inject(LayerMapperService);

    private readonly mapContentsReady = new ReplaySubject<void>(0);
    private readonly mapFreehandService = inject(MapFreehandService);
    private readonly mapPopupService = inject(MapPopupService);
    private readonly mapQueryService = inject(MapQueryService);
    private readonly mapSelectService = inject(MapSelectService);
    private readonly mapSettingsSubject = new BehaviorSubject<IMapSettings>({});
    private readonly mapSketch2Service = inject(MapSketch2Service);
    private readonly mapUtilsService = inject(MapUtilsService);
    private readonly msalAuthService = inject(MsalService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly persistService = inject(PersistService);
    private readonly spatialService = inject(SpatialService);
    private readonly symbolService = inject(SymbolService);
    private readonly widgetList = [];
    private abortController: AbortController;

    private currentSubsetId = "";
    private debounceGoto: any;
    private defaults: IDefaults;

    constructor() {
        const tooltipConfig = inject(NgbTooltipConfig);

        tooltipConfig.container = "body";
        this.abortController = new AbortController();
    }

    @Input() set settings(value: IMapSettings) {
        this.mapSettingsSubject.next(value);
    }

    addGeometries(layerId: string, geometries: IMapGeometry[], goto = false) {
        const layer = this.getLayer(layerId);
        geometries.forEach(g => layer.geometries.push(g));
        this.refreshGeometries(layerId);
        if (goto) {
            this.gotoAll([layerId]);
        }
    }

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    clearAllHighlights() {
        this.highlight?.forEach(hl => {
            if (hl.onRemove) {
                hl.onRemove();
            }
            hl.highlight.remove();
        });
        this.highlight?.splice(0, this.highlight.length);
    }

    clearClass(): void {
        this.mapClasses.splice(0);
    }

    clearGeometries(layerId: string) {
        this.getLayer(layerId).geometries = [];
        this.refreshGeometries(layerId);
    }

    clearLayerFocus() {
        this.focusLayers.splice(0);
    }

    clearPreview(): void {
        this.view.graphics.removeAll();
    }

    clearSelection() {
        this.mapSelectService.selectAll(false);
    }

    closePopups() {
        this.mapPopupService.close();
        this.mapPopupComponent().target = null;
    }

    closeWidget(widgetId: string): void {
        const widget = this.getWidget(widgetId);
        if (widget) {
            widget?.remove();
        }
    }

    closeWidgets() {
        this.widgetList.filter(w => w.closeOnChange).forEach(widgetItem => {
            if (this.widgetContainer().nativeElement.querySelector(`#${widgetItem.id}`)) {
                widgetItem.widget?.remove();
            }
        });
        this.clearClass();
    }

    createDrawLayer(layerid: string = null): IMapDrawLayer {
        const id = layerid ?? GuidUtils.newGuid();
        const newgraphiclayer = new GraphicsLayer({
            title: id,
            visible: true,
            id: id
        });
        this.graphicsLayers.push(newgraphiclayer);
        this.view.map.add(newgraphiclayer);
        const drawlayer = {
            id: id,
            geometries: [],
            visible: true,
            readonly: true,
            hideInLayerList: true
        } as IMapDrawLayer;
        this.mapsettings.drawLayers.push(drawlayer);
        return drawlayer;
    }

    createMediaLayer(mediaLayer: IMapMediaLayerImage, i: number) {
        return new MediaLayer({
            title: mediaLayer.title ? mediaLayer.title : `Medialayer${i}`,
            visible: true,
            minScale: mediaLayer.minScale ?? 0,
            maxScale: mediaLayer.maxScale ?? 0,
            id: mediaLayer.id ? mediaLayer.id : i,
            source: new ImageElement({
                image: mediaLayer.imgUrl,
                georeference: new ExtentAndRotationGeoreference({
                    extent: mediaLayer.extent
                })
            })
        });
    }

    deletePreview(geometryIds: string[]): void {
        geometryIds.forEach(geomid => {
            const index = this.view.graphics.findIndex(x => x.attributes.id === geomid);
            if (index > -1) {
                this.view.graphics.removeAt(index);
            }
        });
    }

    deselectAll() {
        this.mapSelectService.removeAllSelections();
        this.toggleWidget("select");
    }

    deselectGeometries(layerid: string, ids: any[], signal = true) {
        const geometries = this.getLayer(layerid).geometries.filter(g => ids.some(id => id === g.id));
        this.mapSelectService.deselect(layerid, geometries, signal);
    }

    filterGeometries(layers: string[], ids: any[]) {
        layers.forEach(layerid => {
            const layer = this.getLayer(layerid);
            layer.geometries.forEach(g => {
                if (ids.some(id => id === g.id)) {
                    g.visible = true;
                } else {
                    g.visible = false;
                }
            });
            this.showVisibleGeometries(layerid);
        });
    }

    filterLayer(layerid: string, expression: string, sublayerids: string[] = null) {
        const arcgisview = this.getArcgisLayerView(layerid);
        const layer = arcgisview?.layer as any;
        if (arcgisview == null || arcgisview === undefined || layer?.allSublayers?.length <= 0) {
            this.setPassiveFilter(layerid, expression, sublayerids);
            return;
        }
        if (this.hasDefinitionExpressionSupport(layer) === false) {
            console.error(`filterLayer: ${layerid} does not support definitionExpression`);
            return;
        }
        const arcgisSublayers = ObjectUtils.hasValue(sublayerids) ?
            layer.allSublayers.filter(sublayer => sublayerids.some(sl => Number(sl) === sublayer.id || sl === sublayer.title)) :
            layer.allSublayers;
        if (arcgisSublayers?.length > 0) {
            arcgisSublayers.forEach(arcgisSublayer => {
                arcgisSublayer.definitionExpression = expression;
            });
        } else {
            this.showVisibleGeometries(layerid);
        }
    }

    getDefaultSketchLayer() {
        return this.mapsettings.drawLayers.find(x => !x.readonly && x.geometryTypes?.length > 0);
    }

    getExtent(): MapExtent {
        const extent = this.view.extent;
        return {
            minX: extent.xmin,
            minY: extent.ymin,
            maxX: extent.xmax,
            maxY: extent.ymax,
            sr: extent.spatialReference
        };
    }

    getGeometriesByGeometry(layerid: string, selector: IMapGeometry): IMapGeometry[] {
        const arcGraphic = this.spatialService.toArcGis(selector);
        const geometries = this.spatialService.findGeometriesWithin(this.getArcgisLayer(layerid), arcGraphic);
        return geometries.map(g => this.spatialService.toRask(g));
    }

    getGraphics(arcgisLayer: any, geometryIds: any[] = null) {
        if (geometryIds?.length > 0) {
            return arcgisLayer.graphics.items.filter(x => geometryIds.some(g => g === x.attributes.id));
        }
        return arcgisLayer.graphics;
    }

    getHeaderTitle(): string {
        return this.mapsettings.header && this.mapsettings.header.title ? this.mapsettings.header.title : "";
    }

    getMapSettings(): IMapSettings {
        return this.mapsettings;
    }

    getScaleLevel(): number {
        return this.view.scale;
    }

    getSelectedGeometryIds(layerid: string): any[] {
        if (this.mapSelectService.hasSelectionOnLayer(layerid)) {
            return this.mapSelectService.getSelectedOnLayer(layerid).map(x => x.id);
        }
        return [];
    }

    getSketch(): IMapSketch {
        if (!this.sketch) {
            this.sketch = new MapSketch(this, this.symbolService, this.mapPopupService, this.spatialService, this.mapUtilsService, this.ngZone);
        }
        return this.sketch;
    }

    getSubset(): any {
        return this.currentSubsetId;
    }

    getZoomLevel(): number {
        return this.view.zoom;
    }

    gotoAll(layerids: string[] = null) {
        const graphics = this.getAllGraphicsFromLayers(layerids);
        this.gotoGraphics(graphics);
    }

    gotoAllOrDefault(layerids: string[] = null): void {
        const graphics = this.getAllGraphicsFromLayers(layerids);
        if (!graphics || graphics.length === 0) {
            this.gotoExtent(this.defaultExtent);
        } else {
            this.gotoGraphics(graphics);
        }
    }

    gotoCoordinate(swerefX: number, swerefY: number, scale = 3): void {
        const point = new Point({
            x: swerefX,
            y: swerefY,
            spatialReference: this.spatialService.getSpatialReference()
        });
        this.view.goTo({
            target: point,
            scale: scale
        }, {
            animate: true,
            duration: 1000,
            easing: "ease"
        });
    }

    gotoExtent(extent: MapExtent, smooth = true) {
        const graphic1 = {
            geometry: {
                type: "point",
                x: extent.maxX,
                y: extent.maxY,
                spatialReference: this.spatialService.getSpatialReference()
            }
        };
        const graphic2 = {
            geometry: {
                type: "point",
                x: extent.minX,
                y: extent.minY,
                spatialReference: this.spatialService.getSpatialReference()
            }
        };
        this.gotoGraphics([
            graphic1,
            graphic2
        ], smooth);
    }

    gotoExtentDeprecated(xmin: number, ymin: number, xmax: number, ymax: number) {
        this.gotoExtent({
            minX: xmin,
            minY: ymin,
            maxX: xmax,
            maxY: ymax
        } as MapExtent);
    }

    gotoGeometries(geometries: IMapGeometry[], smooth = true) {
        const graphics = geometries.map(g => (this.spatialService.toArcGis(g)));
        const extent = this.spatialService.getExtent(graphics);
        return this.gotoExtent(extent, smooth);
    }

    gotoGeometry(geometry: IMapGeometry, layer: string) {
        const graphics = [];
        const geo = this.getArcgisLayer(layer)?.graphics.find(g => g.attributes.id === geometry.id);
        graphics.push(geo);
        this.gotoGraphics(graphics);
    }

    gotoSelectedOrDefault() {
        if (this.mapSelectService.hasSelection()) {
            this.gotoGeometries(this.mapSelectService.getSelected());
        } else {
            this.gotoAllOrDefault();
        }
    }

    hasDrawLayer(): boolean {
        const anyDrawlayer = this.mapsettings.drawLayers?.filter(x => !x.readonly);
        return anyDrawlayer && anyDrawlayer.length > 0;
    }

    hasEditableDrawLayers(): boolean {
        return this.hasGraphicLayers() && this.mapsettings.drawLayers.filter(x => !x.readonly && this.isLayerEnabled(x.id)).length > 0;
    }

    hasFeatureLayers(): boolean {
        return this.mapsettings.featurelayers != null;
    }

    hasGraphicLayers(): boolean {
        return this.mapsettings.drawLayers != null && this.mapsettings.drawLayers.length > 0;
    }

    hasGraphicsOrFeatureLayers(): boolean {
        return this.hasGraphicLayers() || this.hasFeatureLayers();
    }

    hasHeader(): boolean {
        return this.mapsettings.header != null;
    }

    hasMediaLayers(): boolean {
        return this.mapsettings.mediaLayers != null && this.mapsettings.mediaLayers.length > 0;
    }

    hasMultipleBasemaps(): boolean {
        return this.mapsettings.baselayers && this.mapsettings.baselayers.length > 1;
    }

    hasSelection() {
        return this.mapSelectService.hasSelection();
    }

    hasZoomableGraphicLayers(): boolean {
        return false;
    }

    hideDrawLayer(layerid) {
        this.setDrawLayerVisibility(layerid, false);
    }

    hideFeatureLayer(layerid) {
        this.setFeatureLayerVisibility(layerid, false);
    }

    hideMediaLayer(layerid) {
        this.setMediaLayerVisibility(layerid, false);
    }

    highlightGeometries(layerid: string, geometries: IMapGeometry[]) {
        if (geometries && geometries.some(g => !!g)) {
            const arcgisGeometries = this.getArcgisGraphicsFromIds(layerid, geometries.map(g => g.id));
            if (arcgisGeometries?.length > 0) {
                this.highlightGraphics(layerid, arcgisGeometries.toArray());
            }
        }
    }

    initBaseMaps(settings: IMapSettings) {
        const sr = this.spatialService.getSpatialReference();
        const mapEl = this.arcgisMap().nativeElement;
        // basemaps
        const basemaps = this.getCreateLayers(settings.baselayers).map(rasklayer => new Basemap({
            thumbnailUrl: rasklayer.thumbnailUrl,
            id: rasklayer.id,
            title: rasklayer.name,
            spatialReference: sr,
            baseLayers: [
                new TileLayer({
                    url: rasklayer.url,
                    title: rasklayer.name,
                    visible: rasklayer.visible,
                    id: rasklayer.id,
                    legendEnabled: true
                })
            ]
        }));

        const map = new CoreMap({ basemap: basemaps[0] });
        mapEl.map = map;
        const view = mapEl.view as MapView;
        view.constraints = {
            lods: preloadedMetriaLODs,
            rotationEnabled: false
        };
        view.center = settings.center;
        view.zoom = settings.zoom;
        view.spatialReference = sr;
        view.highlights = [
            {
                color: new Color([
                    253,
                    219,
                    168,
                    1
                ]),
                fillOpacity: 0.6,
                haloColor: new Color([
                    0,
                    0,
                    0,
                    1
                ]),
                haloOpacity: 0.9
            }
        ];

        this.view = view;
        if (basemaps.length > 1) {
            const toggle = this.basemapToggle().nativeElement;
            toggle.nextBasemap = basemaps[1];
            toggle.showTitle = false;
        }
        return map;
    }

    initEsriConfig() {
        esriConfig.request.proxyUrl = environment.mapProxy;
        if (environment.arcgis4.useProxyRules) {
            environment.arcgis4.proxyPrefixes.forEach(prefix => {
                urlUtils.addProxyRule({
                    proxyUrl: environment.mapProxy,
                    urlPrefix: prefix
                });
            });
        }

        esriConfig.geometryServiceUrl = environment.geometryServer;
        esriConfig.request.timeout = 10000;
        esriConfig.request.interceptors.push({
            before: async (params) => {
                if (params.url.includes("sveaskog.se") || params.url.includes("localhost")) {
                    const auth: AuthenticationResult = await firstValueFrom(this.msalAuthService.acquireTokenSilent({ scopes: environment.apiConfig.scopes }));
                    params.requestOptions.headers = params.requestOptions.headers || {};
                    params.requestOptions.headers.Authorization = `Bearer ${auth.accessToken}`;
                }
            }
        });
    }

    initFeatureLayers(settings: IMapSettings) {
        if (!this.hasFeatureLayers()) {
            return;
        }
        const featureLayers = this.getCreateLayers(settings.featurelayers)
            .map(raskFeatureLayer => {
                if (raskFeatureLayer.layerType === LayerType.wms) {
                    return new WMSLayer({
                        url: raskFeatureLayer.url,
                        id: raskFeatureLayer.id,
                        title: raskFeatureLayer.name,
                        visible: this.persistService.isVisible(raskFeatureLayer.id, this.defaults),
                        opacity: raskFeatureLayer.opacity,
                        minScale: raskFeatureLayer.minScale ?? 0,
                        maxScale: raskFeatureLayer.maxScale ?? 0,
                        listMode: raskFeatureLayer.hideInLayerList === true ? "hide" : "show"
                    });
                } else {
                    return new MapImageLayer({
                        url: raskFeatureLayer.url,
                        id: raskFeatureLayer.id,
                        title: raskFeatureLayer.name,
                        visible: this.persistService.isVisible(raskFeatureLayer.id, this.defaults),
                        opacity: raskFeatureLayer.opacity,
                        minScale: raskFeatureLayer.minScale ?? 0,
                        maxScale: raskFeatureLayer.maxScale ?? 0,
                        listMode: raskFeatureLayer.hideInLayerList === true ? "hide" : "show"
                    });
                }
            });
        featureLayers.forEach(featurelayer => {
            featurelayer.visible = false;
        });
        featureLayers.forEach(featurelayer => this.featureLayers.push(featurelayer));
    }

    initGraphicLayers(settings: IMapSettings) {
        if (!this.hasGraphicLayers()) {
            return;
        }

        let i = 0;
        const layers = [...settings.drawLayers];
        const drawLayers = [...layers]
            .sort((left, right) => (this.getSortIndexForLayer(right)) - (this.getSortIndexForLayer(left)))
            .map(drawlayer => {
                i++;
                return new GraphicsLayer({
                    title: drawlayer.title ? drawlayer.title : `Lager${i}`,
                    visible: drawlayer.visible ?? this.persistService.isVisible(drawlayer.id, this.defaults),
                    minScale: drawlayer.minScale ?? 0,
                    maxScale: drawlayer.maxScale ?? 0,
                    listMode: drawlayer.hideInLayerList === true ? "hide" : "show",
                    id: drawlayer.id ? drawlayer.id : i
                });
            });

        drawLayers.forEach(graphicslayer => {
            this.graphicsLayers.push(graphicslayer);
            this.arcgisGeometries.set(graphicslayer.id, []);
        });

        this.initSketchWidget(settings);

        this.initConversionWidget(new CoordinateVM({
            view: this.view
        }));
    }

    initMediaLayers(settings: IMapSettings) {
        if (!this.hasMediaLayers()) {
            return;
        }

        let i = 0;
        const layers = [...settings.mediaLayers];
        const mediaLayers = [...layers]
            .filter(ml => ml.visible)
            .sort((left, right) => (this.getSortIndexForLayer(right)) - (this.getSortIndexForLayer(left)))
            .map(mediaLayer => {
                i++;
                return mediaLayer.mediaOptions
                    .filter(mo => mo.selected)
                    .map(mo => this.createMediaLayer(mo, i));
            });

        mediaLayers.forEach(mediaLayerContainer => {
            mediaLayerContainer.forEach(mediaLayer => {
                this.mediaLayers.push(mediaLayer);
            });
        });
    }

    initSketchWidget(settings) {
        const sketchWidgetItem = {
            id: "sketch",
            widget: undefined,
            closeOnChange: true
        };
        this.widgetList.push(sketchWidgetItem);
        this.getSketch().init(this.graphicsLayers, this.view);

        const defaultSketchLayer = this.getDefaultSketchLayer();
        if (defaultSketchLayer) {
            this.getSketch().startSketch({ drawLayer: defaultSketchLayer });
        }

        this.mapSketch2Service.init(this.view, this);
    }

    initSubComponents() {
        this.mapSubComponents()?.forEach(component => {
            component.onInit(this.view, this.draw);
        });
    }

    initializeMap(settings: IMapSettings) {
        this.setMapHeight();

        this.mapContentsReady.pipe(
            takeUntil(this.ngUnsubscribe$),
            tap(() => this.onMapContentsReady(settings))
        ).subscribe();
    }

    isAllFeatureLayersReady() {
        return (this.view ?? false) && this.allLayersLoaded;
    }

    isArcGisApiLoaded(): boolean {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const globalRequire: any = window["require"];
        return globalRequire && globalRequire.on;
    }

    isLayerEnabled(layerId): boolean {
        const subComponent = this.mapSubComponents().find(c => !!c.drawLayers.find(l => l.id === layerId));
        return subComponent ? subComponent.enabled : true;
    }

    isLayerVisible(layerid: string): boolean {
        const layerView = this.view.layerViews.find(x => x.layer.id === layerid);
        if (!layerView) {
            return false;
        }

        if (this.hasSublayerVisibility(layerView.layer)) {
            const subLayerVisibilities = (layerView.layer as MapImageLayer).sublayers.map(x => x.visible);
            return subLayerVisibilities.some(x => x === true);
        }
        return layerView.layer.visible && layerView.visible;
    }

    isPopupBlocked(): boolean {
        return this.mapPopupService.popupBlocked;
    }

    isSelecting() {
        return this.mapSelectService.isSelecting;
    }

    isWidgetActive(widgetId) {
        return !!this.view.ui.find(widgetId);
    }

    isWidgetsActive(widgetIds: string[]): boolean {
        return widgetIds.some(wid => this.view.ui.find(wid) != null);
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.ngZone.runOutsideAngular(() => {
                this.mapContentsReady.next();
                this.mapNavigateComponent().map = this;
            });

            if (ObjectUtils.hasValue(this.mapsettings.defaultWidgetId)) {
                this.toggleWidget(this.mapsettings.defaultWidgetId);
            }
        }, 0);
    }

    ngOnDestroy() {
        this.esriHandlers?.forEach(x => x.remove());
        this.esriHandlers = [];
        if (this.view) {
            this.view.container = null;
            this.view.map = null;
            this.view.destroy();
        }
        this.abortController.abort();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.mapSettingsSubject.pipe(
                takeUntil(this.ngUnsubscribe$)
            ).subscribe(settings => {
                this.mapsettings = settings;
                this.setMapSettingsDefaults();
                try {
                    this.initializeMap(this.mapsettings);
                } catch (e) {
                    console.error(e);
                }
                this.persistService.load(settings.id).then((defaults) => {
                    this.defaults = defaults;
                });
            });
            if (this.refresh) {
                this.refresh.pipe(
                    takeUntil(this.ngUnsubscribe$)
                ).subscribe(params => {
                    this.refreshGeometries(params.layerId);
                });
            }
            if (this.filter) {
                this.filter.pipe(
                    takeUntil(this.ngUnsubscribe$)
                ).subscribe(params => {
                    this.filterLayer(params.layerId, params.filterExpression, params.sublayerId ? [params.sublayerId] : undefined);
                });
            }
        });
    }

    onMapContentsReady(settings: IMapSettings) {
        this.initEsriConfig();

        const map = this.initBaseMaps(settings);

        // load symbols
        if (this.mapsettings.icons) {
            this.symbols = this.mapsettings.icons.map(i => this.symbolService.fromIcon(i));
        }

        if (!this.hasHeader()) {
            this.view.ui.padding = {
                top: 38,
                right: 4
            };
        }

        // subComponents
        this.setupSubComponents();

        // feature layers
        this.initFeatureLayers(settings);

        // graphic layers
        this.initGraphicLayers(settings);

        // media layers
        this.initMediaLayers(settings);

        this.draw = new Draw({ view: this.view });
        this.mapSelectService.init(this.mapsettings, this.view, this.draw, null);
        this.mapQueryService.init(this.mapsettings, this.view);
        this.mapFreehandService.init(this.mapsettings, this.view, this.draw, null);

        this.initSubComponents();

        // subsets
        if (this.mapsettings.subset) {
            this.setSubset(this.mapsettings.initalSubset);
        } else {
            this.setAllLayers();
        }

        if (this.mapsettings.coords) {
            this.createCoordsWidget();
        }

        // events and watches
        this.registerListeners(map);
    }

    @HostListener("window:resize", ["$event"]) onWindowResize(event) {
        this.setMapHeight();
    }

    openMenu(widgetId: string) {
        this.widgetMenuButtonClick(this.widgetMenuButtons.find(w => w.widgetId === widgetId));
    }

    openWidget(widgetId: string): void {
        const widget = this.getWidget(widgetId);
        if (widget) {
            this.widgetContainer().nativeElement.append(widget);
        }
    }

    preview(geometries: IMapGeometry[], color?: any, clearFirst = true, timeout = null, goto = null): Graphic[] {
        if (clearFirst) {
            this.view.graphics.removeAll();
        }
        const graphics = geometries.map(g => (this.spatialService.toArcGis(g, color)));
        this.view.graphics.addMany(graphics);
        if (timeout != null) {
            setTimeout(() => {
                const removable = this.view.graphics.filter(gfx => geometries.some(g => g.id === gfx.attributes.id));
                this.view.graphics.removeMany(removable);
            }, timeout);
        }
        if (goto === true) {
            setTimeout(() => {
                this.view.goTo(this.view.graphics.toArray());
            }, 50);
        }
        return this.view.graphics.toArray();
    }

    refreshGeometries(layerid: string) {
        this.renderGeometries({ layerId: layerid });
        this.getSketch().updateSketchWidget(layerid);
        this.refreshSubComponents(layerid);
    }

    refreshGraphics(layerid: string, geometries: IMapGeometry[]) {
        const graphics = this.getArcgisGraphicsFromIds(layerid, geometries.map(x => x.id));
        geometries.forEach(rask => {
            const graphic = graphics.find(x => x.attributes.id === rask.id);
            if (graphic) {
                let symbol;
                if (rask.iconid) {
                    symbol = this.symbolService.fromIcon(this.mapsettings.icons.find(x => x.id === rask.iconid), rask.selected);
                } else {
                    symbol = this.symbolService.cloneSymbol(graphic.symbol);
                    symbol.color = rask.color;
                }
                graphic.symbol = symbol;
                graphic.setAttribute("selected", rask.selected);
            }
        });
    }

    refreshSubComponents(layerid: string) {
        this.mapSubComponents()?.forEach(c => c.onLayerRefresh?.next(layerid));
    }

    removeDrawLayer(layerid: string) {
        const gl = this.graphicsLayers.find(x => x.id === layerid);
        remove(this.graphicsLayers, x => x.id === layerid);
        remove(this.mapsettings.drawLayers, x => x.id === layerid);
        this.view.map.remove(gl);
    }

    removeMediaLayer(layerid: string) {
        const ml = this.mediaLayers.find(x => x.id === layerid);
        remove(this.mediaLayers, x => x.id === layerid);
        remove(this.mapsettings.mediaLayers, x => x.id === layerid);
        this.view.map.remove(ml);
    }

    reorderGraphicsBySize(arcGraphics: Collection<Graphic>) {
        arcGraphics.sort((left, right) => this.getSquareKilometers(right) - this.getSquareKilometers(left));
    }

    reorderGraphicsByVisibility(arcGraphics: Collection<Graphic>) {
        // Gör så att alla synliga geometrier hamnar sist i Collectionen
        arcGraphics.sort((left, right) => Number(right.visible) - Number(left.visible));
    }

    reorderPolygonsBySize(layerid) {
        const layer = this.getArcgisLayer(layerid);
        this.reorderGraphicsBySize(layer.graphics);
    }

    selectGeometries(layerid: string, ids: any[], signal = true) {
        const geometries = this.getLayer(layerid).geometries.filter(g => ids.some(id => id === g.id));
        this.mapSelectService.select(layerid, geometries, signal);
    }

    setAllLayers() {
        this.clearAllHighlights();
        this.view.map.removeAll();
        const all = this.featureLayers.concat(this.mediaLayers).concat(this.graphicsLayers);
        this.setLayers(all);
    }

    setClass(classes: string[]): void {
        this.clearClass();
        classes.forEach(c => this.mapClasses.push(c));
    }

    setGeometries(layerId: string, geometries: IMapGeometry[], goto = false) {
        const layer = this.getLayer(layerId);
        layer.geometries = [];
        this.addGeometries(layerId, geometries, goto);
    }

    setLayerFocus(layerids: string[]) {
        this.clearLayerFocus();
        layerids.forEach(l => {
            this.focusLayers.push(l);
        });
    }

    setLayers(layers: any[]) {
        this.allLayersLoaded = false;
        this.view.map.addMany(layers);
        this.notifyWhenAllLayersReady();
    }

    setMapHeight(height?: string) {
        if (!height && (this.mapsettings.height && this.mapsettings.height === "100%")) {
            height = `${window.innerHeight - this.mapsettings.mapVerticalStart}px`;
        } else if (!height && this.mapsettings.height) {
            height = this.mapsettings.height;
        }
        const actualHeight = height.endsWith("px") ? height : `${height}px`;
        this.arcgisMap().nativeElement.setAttribute("style", `height: ${actualHeight}; width: 100%`);
    }

    setSubset(id: any) {
        if (id === this.currentSubsetId || !this.mapsettings.subset.some(x => x.id === id)) {
            return;
        }
        this.clearAllHighlights();
        this.view.map.removeAll();
        const layers = this.mapsettings.subset.find(x => x.id === id).layerIds.map(s => this.getFeatureOrGraphicsArcgisLayer(s));
        this.currentSubsetId = id;
        this.setLayers(layers);
    }

    showDrawLayer(layerid) {
        this.setDrawLayerVisibility(layerid, true);
    }

    showFeatureLayer(layerid: string): Observable<boolean> {
        return this.setFeatureLayerVisibility(layerid, true);
    }

    showMediaLayer(layerid) {
        const mediaOptions = this.getMediaSubLayer(layerid);
        const arcgisLayer = this.getArcgisMediaLayer(layerid);

        if (!arcgisLayer) {
            const newMediaLayer = this.createMediaLayer(mediaOptions, this.mediaLayers.length + 1);
            this.mediaLayers.push(newMediaLayer);
            this.view.map.add(newMediaLayer);
            this.view.map.layers.reorder(newMediaLayer, 1);
        } else {
            this.setMediaLayerVisibility(layerid, true);
        }
        this.setMediaLayerVisibility(layerid, true);
    }

    showPopup(innerComponent: any, mouseEvent: ScreenPoint, data: any, geometry: IMapGeometry): Observable<ComponentRef<any>> {
        this.mapPopupComponent().target = geometry;
        return this.mapPopupService.show(innerComponent, mouseEvent, data, this.mapPopupComponent(), this);
    }

    showPopupOnGeometry(innerComponent: any, geometry: IMapGeometry, data?: any): Observable<any> {
        return this.showPopup(innerComponent, this.getGeometryScreenPosition(geometry), data, geometry);
    }

    toggleFullscreen() {
        if (!document.fullscreenElement) {
            this.arcgisMap().nativeElement.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    }

    toggleWidget(widgetId: string, closeOthers = true) {
        const widget = this.getWidget(widgetId);
        if (widget) {
            const isActive = this.widgetContainer().nativeElement.querySelector(`#${widgetId}`);

            if (!isActive) {
                if (closeOthers) {
                    this.closeWidgets();
                }
                this.widgetContainer().nativeElement.append(widget);
            } else {
                widget.remove();
            }
        }
    }

    updateCoordsWidget(event: IMapMouse) {
        this.ngZone.run(() => {
            this.coordsViewModel.x = event.point.x;
            this.coordsViewModel.y = event.point.y;
        });
    }

    updateGeometry(geometry: IMapGeometry, layerId?: string, removeGeo = false) {
        if (!layerId) {
            layerId = this.getLayerFromGeometry(geometry).id;
        }
        this.removeGeometry(layerId, geometry);

        if (!removeGeo) {
            this.addGeometry(layerId, geometry);
        }
    }

    updateGeometryAttributes(raskGeometry: IMapGeometry) {
        const graphic = this.getArcGisGraphicsItem(raskGeometry);
        this.spatialService.updateArcGraphic(graphic, raskGeometry as any);
    }

    updatePopupPosition() {
        if (ObjectUtils.hasValue(this.mapPopupComponent()?.target)) {
            this.mapPopupService.updatePosition(this.getGeometryScreenPosition(this.mapPopupComponent().target));
        }
    }

    updatePreview(geometries: IMapGeometry[], insertIfNeeded?: boolean, color?: any): void {
        if (!geometries || geometries.length === 0) {
            return;
        }
        const updatedGraphics = geometries.map(g => (this.spatialService.toArcGis(g, color)));
        updatedGraphics.forEach(updatedGraphic => {
            const index = this.view.graphics.findIndex(x => x.attributes.id === updatedGraphic.attributes.id);
            if (index > -1) {
                this.view.graphics.splice(index, 1, updatedGraphic);
            } else if (insertIfNeeded) {
                this.view.graphics.add(updatedGraphic);
            }
        });
    }

    widgetMenuButtonClick(btn: IWidgetMenuButton) {
        if (btn.disabled) {
            return;
        }
        this.toggleWidget(btn.widgetId);
        if (btn.click) {
            btn.click();
        }
    }

    protected isButtonActive(btn: IWidgetMenuButton) {
        return !!this.view.ui.find(btn.widgetId);
    }

    protected isButtonVisible(btn: IWidgetMenuButton) {
        return btn.visible === true || (btn.visible && btn.visible());
    }

    private addGeometry(layerId: string, geometry: IMapGeometry) {
        const raskLayer = this.getLayer(layerId);
        const arcgisGeometry = this.spatialService.toArcGis(geometry, raskLayer.color, this.symbols, this.mapsettings.icons);
        this.arcgisGeometries.get(layerId).push(arcgisGeometry);

        if (geometry.visible) {
            const arcgisLayer = this.getArcgisLayer(layerId);
            arcgisLayer.add(arcgisGeometry);
        }
    }

    private addLayerCreateError(layer: Layer) {
        if (!ObjectUtils.isNullOrUndefined(layer)) {
            const errors = _.cloneDeep(this.layerCreationErrorMessageSource.value);
            if (!errors.includes(layer.title)) {
                errors.push(layer.title);
                this.layerCreationErrorMessageSource.next(errors);
            }
        }
    }

    private clickHitTest(event: ArcGisClickEvent) {
        this.mapPopupService.close();

        this.hitTest(event, "immediate-click", (mouse, hitlist) => {
            this.signalMouseEvent(mouse);
            if (this.mapsettings.onMapClick) {
                this.mapsettings.onMapClick(mouse, hitlist);
            }

            if (event.button === MapMouseData.Left) {
                this.leftMouseClicked(event, mouse, hitlist);
            } else if (event.button === MapMouseData.Right) {
                this.rightMouseClicked(event, hitlist);
            }
        });
    }

    private compareGeometry(a: Graphic, b: IMapGeometry) {
        return (a.attributes.id === b.id || a.attributes.geoid === b.geoid);
    }

    private compareRaskGeometry(a: IMapGeometry, b: IMapGeometry) {
        return (a.id === b.id || a.geoid === b.geoid) && a.parent?.id === b.parent?.id;
    }

    private createCoordsWidget() {
        const coordsWidget = this.coordsWidget().nativeElement;
        this.registerWidget(coordsWidget, "coords");
        coordsWidget.addEventListener("click", event => {
            this.ngZone.run(() => {
                this.coordsViewModel.toggleMenu();
            });
        });
    }

    private enableFilter(arcgisLayer: any) {
        const featureLayer = this.mapsettings.featurelayers.find(x => x.id === arcgisLayer.id);
        if (ObjectUtils.hasValue(featureLayer?.filter)) {
            this.filterLayer(featureLayer.id, featureLayer.filter.expression, featureLayer.filter.sublayerids);
        }
    }

    private ensureVisible(sublayer: any) {
        let current = sublayer.parent;
        while (current !== undefined) {
            current.visible = true;
            current = current.parent;
        }
    }

    private getAllGeometriesInExtent(layer: IMapDrawLayer): IMapGeometry[] {
        const arcgisLayer = this.getArcgisLayer(layer.id);
        if (arcgisLayer.visible) {
            const graphicsInView = [];
            arcgisLayer.graphics.forEach(graphic => {
                if (this.view.extent && this.view.extent.contains(graphic.geometry as Point)) {
                    graphicsInView.push(graphic);
                }
            });
            const raskgeometries = this.getRaskGeometriesFromGraphics(graphicsInView, layer, layer.id);
            return raskgeometries.filter(x => x.visible);
        }
        return [];
    }

    private getAllGraphicsFromLayers(layerids: string[] = null): Graphic[] {
        const graphics = [];
        const layers = layerids ? layerids : this.mapsettings.drawLayers.map(dl => dl.id);
        layers?.forEach(id => {
            this.getArcgisLayer(id)?.graphics.forEach(g => {
                graphics.push(g);
            });
        });
        return graphics;
    }

    private getArcGisGraphicsItem(geometry: IMapGeometry) {
        let found = null;
        this.graphicsLayers.forEach((gl) => {
            found = gl.graphics?.find(i => i.attributes.id === geometry.id || i.attributes.geoid === geometry.geoid) || found;
        });
        return found;
    }

    private getArcgisGraphicsFromIds(layerid: string, ids: any[]) {
        const layer = this.getArcgisLayer(layerid);
        return layer.graphics.filter(g => ids.some(id => id === g.attributes.id));
    }

    private getArcgisLayer(id: string) {
        return this.graphicsLayers.find(x => x.id === id);
    }

    private getArcgisLayerView(id: string): LayerView {
        return this.view.allLayerViews.find(x => x.layer.id === id);
    }

    private getArcgisMediaLayer(id: string): MediaLayer {
        return this.mediaLayers.find(x => x.id === id);
    }

    private getCreateLayers(layers: IMapLayer[]) {
        return layers ? layers.map(layer => this.layerMapperService.getLayer(layer)) : [];
    }

    private getCurrentMapCoordinates() {
        const conversion: CoordinateVM = this.getWidget("conversion");
        return conversion?.conversions?.getItemAt(0)?.position?.location;
    }

    private getFeatureOrGraphicsArcgisLayer(id: any) {
        if (this.graphicsLayers.some(x => x.id === id)) {
            return this.graphicsLayers.find(x => x.id === id);
        } else {
            return this.featureLayers.find(x => x.id === id);
        }
    }

    private getGeometryScreenPosition(geometry: IMapGeometry) {
        try {
            const graphicDest = this.spatialService.convertToGraphic(geometry);
            return this.mapUtilsService.getScreenPosition(graphicDest, this.view);
        } catch { }
        return null;
    }

    private getLayer(id: string) {
        return this.mapsettings.drawLayers.find(x => x.id === id);
    }

    private getLayerFromGeometry(geometry: IMapGeometry) {
        return this.mapsettings.drawLayers.find(l => l.geometries.find(g => this.compareRaskGeometry(g, geometry)));
    }

    private getLayersReadyObserver() {
        const subject = new Subject<void>();
        const promises: Promise<LayerView>[] = [];
        this.view.map.layers.forEach(layer => {
            promises.push(this.view.whenLayerView(layer));
        });
        Promise.all(promises).then(() => {
            subject.next();
            subject.complete();
        }, (err) => {
            if (err.name !== "cancelled:layerview-create") {
                console.error(err);
            }
            subject.next();
            subject.complete();
        });
        return subject.asObservable();
    }

    private getMapPoint(screenX, screenY) {
        return this.view.toMap({
            x: screenX,
            y: screenY
        });
    }

    private getMediaSubLayer(id: string) {
        return this.mapsettings.mediaLayers.flatMap(ml => ml.mediaOptions).find(x => x.id === id);
    }

    private getMouseData(event: IArcGisPointerEvent): IMapMouse {
        const point = this.getMapPoint(event.x, event.y);
        const coords = this.getCurrentMapCoordinates();
        return {
            x: event.x,
            y: event.y,
            clientX: event.native.clientX,
            clientY: event.native.clientY,
            screenX: event.native.screenX,
            screenY: event.native.screenY,
            point: point,
            coords: {
                longitude: coords?.longitude,
                latitude: coords?.latitude
            }
        };
    }

    private getRaskGeometriesFromGraphics(arcgisGraphics: any[], rasklayer: IMapDrawLayer = null, layerid: string = null) {
        const layer = rasklayer ? rasklayer : this.getLayer(layerid);
        return (layer.geometries || []).filter(
            raskGeometry => arcgisGraphics.some(arcgisGraphic => arcgisGraphic.attributes.geoid === raskGeometry.geoid || arcgisGraphic.id === raskGeometry.id));
    }

    private getSortIndexForLayer(layer: IMapDrawLayer): number {
        if (layer.customLayerOrderWeight) {
            return layer.customLayerOrderWeight;
        }
        if (layer.drawOptions?.some(x => x.type === "polygon")) {
            return 30;
        }
        if (layer.drawOptions?.some(x => x.type === "polyline")) {
            return 20;
        }
        if (layer.drawOptions?.some(x => x.type === "point")) {
            return 10;
        }
        return 0;
    }

    private getSquareKilometers(graphic: any) {
        return this.spatialService.getAreaFromGraphic(graphic);
    }

    private getWidget(widgetId: string) {
        const widgets = this.widgetList.filter(w => w.id === widgetId);
        return widgets && widgets.length > 0 ? widgets[0].widget : null;
    }

    private gotoGraphics(graphics: any[], smooth = true) {
        if (_.isEmpty(graphics) || this.view.destroyed) {
            return;
        }
        if (!this.abortController) {
            this.abortController = new AbortController();
        }
        clearTimeout(this.debounceGoto);
        this.debounceGoto = setTimeout(() => {
            const options = (smooth ? {
                animate: true,
                duration: 500,
                easing: "ease"
            } : { animate: false }) as GoToOptions2D;
            options.signal = this.abortController.signal;
            this.view.goTo(graphics, options).then(() => undefined, (err) => undefined);

        }, 200);
    }

    private handleHitTestResults(response: any, eventType: string, event: IArcGisPointerEvent, objects: IHitItem[], mouse: IMapMouse) {
        response.results.forEach(result => {
            if (eventType === event.type &&
                result.graphic?.layer?.id &&
                result.graphic?.attributes?.id) {
                let graphic = result.graphic;
                const layer = this.getLayer(graphic.layer.id);
                if (!layer || !this.isFocusedLayer(layer.id)) {
                    return;
                }
                let geometry = layer.geometries.find(x => x.id === graphic.attributes.id);
                if (geometry?.parent) {
                    const arcgisLayer = this.getArcgisLayer(layer.id);
                    const parentGraphic = arcgisLayer.graphics.find(x => x.attributes.id === geometry.parent.id);
                    graphic = parentGraphic;
                    geometry = geometry.parent;
                }
                if (geometry) {
                    objects.push({
                        layer: layer,
                        geometry: geometry,
                        mouse: mouse,
                        graphicslayer: graphic.layer,
                        graphic: graphic
                    });
                } else if (graphic.attributes?.isDrawing) {
                    objects.push({
                        layer: layer,
                        geometry: this.spatialService.toRask(graphic, true),
                        mouse: mouse,
                        graphicslayer: graphic.layer,
                        graphic: graphic
                    });
                }
            }
        });
    }

    private hasDefinitionExpressionSupport(arcLayer: Layer): boolean {
        if ((arcLayer as MapImageLayer).capabilities) {
            return (arcLayer as MapImageLayer).capabilities?.exportMap?.supportsSublayerDefinitionExpression;
        }
        return false;
    }

    private hasQuerySupport(arcLayer: Layer): boolean {
        if ((arcLayer as MapImageLayer).capabilities) {
            return (arcLayer as MapImageLayer).capabilities?.operations?.supportsQuery;
        }
        return false;
    }

    private hasSublayerVisibility(arcLayer: Layer): boolean {
        if ((arcLayer as MapImageLayer).capabilities) {
            return (arcLayer as MapImageLayer).capabilities?.exportMap?.supportsSublayerVisibility;
        }
        return false;
    }

    private highlightGraphics(layerid: string, arcgisgeometries: Graphic[]) {
        const view = this.getArcgisLayerView(layerid) as any;
        const raskLayer = this.getLayer(layerid);
        if (view) {
            let resetVisibility: () => void;
            if (raskLayer.allowInvisibleGeometries) {
                // Listar alla osynliga och sätter de synliga
                const notVisible = arcgisgeometries.filter(x => !x.visible);
                notVisible.forEach(x => x.visible = true);

                // Sorterar om arcgislagrets graphics så att synliga ligger högst upp
                const arcgisLayer = this.getArcgisLayer(layerid);
                this.reorderGraphicsByVisibility(arcgisLayer.graphics);

                // Skapar återställningsmetod
                resetVisibility = () => {
                    notVisible.forEach(x => x.visible = false);
                };
            }
            const arcGisHighlight = view.highlight(arcgisgeometries);
            if (arcGisHighlight) {
                const raskHighlight: IHighlightedGraphic = {
                    highlight: arcGisHighlight,
                    onRemove: resetVisibility
                };

                this.highlight.push(raskHighlight);
            }
        }
    }

    private hitTest(event: IArcGisPointerEvent, eventType: string, geometryHitCallback: (mouse: IMapMouse, hititems: IHitItem[]) => void) {
        this.view.hitTest(event).then((response) => {
            const objects: IHitItem[] = [];
            const mouse = this.getMouseData(event);
            if (response?.results?.length > 0) {
                this.handleHitTestResults(response, eventType, event, objects, mouse);
            }
            geometryHitCallback(mouse, objects);
        });
    }

    private hoverHitTest(event: ArcGisPointerMoveEvent) {
        // Förhindrar att hittest/hover sker vid panorering
        if (!this.view.stationary) {
            return;
        }
        if (this.mapsettings.coords) {
            this.updateCoordsWidget(this.getMouseData(event));
        }

        this.hitTest(event, "pointer-move", (mouse, hitlist) => {
            const newList = this.updateHighlights(hitlist);
            newList?.out?.forEach(hoverOut => {
                this.signalHoverOut(hoverOut.layer, hoverOut.geometry, mouse);
            });
            newList?.in?.forEach(hoverIn => {
                this.signalHoverIn(hoverIn.layer, hoverIn.geometry, mouse);
            });
        });
    }

    private initConversionWidget(widget) {
        this.widgetList.push({
            id: "conversion",
            widget: widget,
            closeOnChange: true
        });
    }

    private registerWidget(element, destID, closeOnChange = false) {
        this.widgetList.push({
            id: destID,
            widget: element,
            closeOnChange: closeOnChange
        });
    }

    private isFocusedLayer(layerid: string): boolean {
        return this.focusLayers.length === 0 || this.focusLayers.some(layer => layer === layerid);
    }

    private isLayerPersisted(layer: Layer) {
        const feature = this.mapsettings.featurelayers.find(fl => fl.id === layer.id);
        return !feature?.hideInLayerList;
    }

    private isUserLayer(layer: Layer) {
        return this.mapsettings.drawLayers?.find(d => d.id === layer.id)
            || this.mapsettings.featurelayers?.find(f => f.id === layer.id)
            || this.mapsettings.baselayers?.find(b => b.id === layer.id);
    }

    private keyDown(event) {
        if (event.key === "Delete" || event.key === "Backspace") {
            if (this.mapSelectService.hasSelection()) {
                this.mapsettings?.drawLayers?.filter(x => x.selectable)?.forEach((layer) => {
                    if (layer.onGeometryDeleted) {
                        const selected = layer.geometries.filter(g => g.selected);
                        if (layer.onGeometryDeleted(selected)) {
                            remove(layer.geometries, (g) => selected.some(s => s.id === g.id || s.geoid === g.geoid));
                            this.refreshGeometries(layer.id);
                            this.mapSelectService.deselect(layer.id, selected);
                        }
                    }
                });
            }
            event.stopPropagation();
        }
    }

    private leftMouseClicked(event: ArcGisClickEvent, mouse: IMapMouse, hitlist: IHitItem[]) {
        if (hitlist?.length > 0) {
            this.processHitlist(event, mouse, hitlist);
        } else {
            this.mapsettings.drawLayers?.filter(layer => layer.onClick && this.isLayerVisible(layer.id) && this.isFocusedLayer(layer.id)).forEach(drawLayer => {
                drawLayer.onClick(mouse, null, event.native.ctrlKey);
            });
            this.mapsettings.featurelayers?.filter(layer => layer.onClick && this.isLayerVisible(layer.id) && this.isFocusedLayer(layer.id)).forEach(featureLayer => {
                featureLayer.onClick(mouse, null, event.native.ctrlKey);
            });
            this.mapsettings.featurelayers?.filter(layer => layer.onClickFeature && this.isLayerVisible(layer.id) && this.isFocusedLayer(layer.id)).forEach(featureLayer => {
                this.mapQueryService.queryAllSublayers(featureLayer.id, mouse.point, null).subscribe(result => {
                    result.forEach(l => featureLayer.onClickFeature(result, mouse, event.native.ctrlKey));
                });
            });
        }
    }

    private notifyWhenAllLayersReady() {
        const subs = this.getLayersReadyObserver().pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(() => {
            if (this.mapsettings.onAllLayersReady) {
                this.mapsettings.onAllLayersReady(this);
            }
            this.view.map.layers.forEach(x => {
                if (x.type === LayerType.wms) {
                    const wmsLayer = x as WMSLayer;
                    wmsLayer.sublayers = wmsLayer.allSublayers;
                }
            });
            this.allLayersLoaded = true;
            this.signalAllLayersReady();
            subs.unsubscribe();
        });
    }

    private processHitlist(event: ArcGisClickEvent, mouse: IMapMouse, hitlist: IHitItem[]) {
        const priohitlist = (hitlist.length === 1 ?
            hitlist :
            // eslint-disable-next-line sonarjs/no-misleading-array-reverse
            hitlist.sort((left, right) => this.getSquareKilometers(left.graphic) - this.getSquareKilometers(right.graphic)));
        for (const item of priohitlist) {
            if (event.native.ctrlKey && item.layer.selectable) {
                this.mapSelectService.toggle([item]);
                break;
            }
            if (item?.layer?.onClick) {
                item.layer.onClick(mouse, item.geometry, event.native.ctrlKey);
                break;
            }
        }
    }

    private registerListeners(map: any) {
        this.registerViewEvents();
        this.registerViewWatchers();
        // listen to incoming events
        this.eventService.subscribeEvent(MapClosingPopupEvent.EVENTNAME).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(closing => {
            this.closePopups();
        });
        // All layers loaded
        this.notifyWhenAllLayersReady();
    }

    private registerViewEvents() {
        this.esriHandlers.push(this.view.on("immediate-click", (event) => {
            this.ngZone.run(() => {
                this.clickHitTest(event);
            });
        }));
        this.esriHandlers.push(this.view.on("pointer-move", (event) => this.hoverHitTest(event)));
        this.esriHandlers.push(this.view.on("key-down", (event) => this.keyDown(event)));
        this.esriHandlers.push(this.view.on("layerview-create", (event) => {
            if (!this.isUserLayer(event.layer)) {
                return;
            }

            this.view.whenLayerView(event.layer).then(() => {
                this.enableFilter(event.layer);
                this.setPersistedVisibility(event.layer);
                this.eventService.emit<LayerReadyEvent>(new LayerReadyEvent(event.layer.id));
                if (this.mapsettings.onLayerReady) {
                    this.mapsettings.onLayerReady(this, event.layer.id);
                }
            });
        }));
        this.layerCreationErrorMessageSource.next([]);
        this.esriHandlers.push(this.view.on("layerview-create-error", (event) => {
            console.error(`Layer: ${event.layer?.id}`);
            console.error(`Error: ${event.error?.message}`);
            this.addLayerCreateError(event.layer);
        }));
    }

    private registerViewWatchers() {
        this.esriHandlers.push(
            reactiveUtils.watch(
                () => (
                    [
                        this.view.scale,
                        this.view.zoom,
                        this.view.stationary
                    ]
                ),
                (
                    [scale, zoom, stationary]
                ) => {
                    this.signalLayersPan();
                    if (this.mapsettings.onMapPan) {
                        this.mapsettings.onMapPan(this,
                            this.view?.zoom,
                            this.view?.scale,
                            {
                                x: this.view?.center?.x,
                                y: this.view?.center?.y
                            },
                            this.view?.extent,
                            this.view?.stationary);
                    }
                }));

        this.esriHandlers.push(
            reactiveUtils.watch(() => (
                [
                    this.view.scale,
                    this.view.zoom,
                    this.view.center
                ]), (_) => { this.updatePopupPosition(); }));

        this.esriHandlers.push(
            reactiveUtils.watch(() => (this.view.ready), (ready) => {
                if (ready) {
                    this.eventService.emit<MapReadyEvent>(new MapReadyEvent(this));
                    if (this.mapsettings.onMapReady) {
                        this.mapsettings.onMapReady(this);
                    }
                    this.mapSubComponents().forEach(s => s.mapReady.emit(this));
                }
            }));
    }

    private removeGeometry(layerId: string, geometry: IMapGeometry) {
        const arcgisLayer = this.getArcgisLayer(layerId);
        const arcgisGeometry = arcgisLayer.graphics.filter(g => this.compareGeometry(g, geometry));
        if (!arcgisGeometry || arcgisGeometry.length === 0) {
            return;
        }
        arcgisLayer.removeMany(arcgisGeometry.toArray());

        const arcgisAllLayerGeometries = this.arcgisGeometries.get(layerId);
        arcgisGeometry.forEach(g => {
            const index = arcgisAllLayerGeometries.indexOf(g);
            if (index > -1) {
                arcgisAllLayerGeometries.splice(index, 1);
            }
        });
    }

    private renderGeometries(params: IRenderParams) {
        // https://community.esri.com/thread/245932-graphics-layer-item-limit-rendering-problem-in-api-version-412
        const arcgisLayer = this.getArcgisLayer(params.layerId);
        const raskLayer = this.getLayer(params.layerId);
        if (!arcgisLayer || !raskLayer) {
            return;
        }
        const arcgisGraphics = raskLayer.geometries?.map(geometry => this.spatialService.toArcGis(geometry, raskLayer.color, this.symbols, this.mapsettings.icons));
        this.arcgisGeometries.set(params.layerId, arcgisGraphics);
        this.showVisibleGeometries(params.layerId);
        // reflect "new" extent when geometries changes
        if (raskLayer.onPan) {
            const geosInView = this.getAllGeometriesInExtent(raskLayer);
            raskLayer.onPan(geosInView);
        }
    }

    private rightMouseClicked(event: ArcGisClickEvent, hitlist: IHitItem[]): void {
        const raskPoint = this.spatialService.toRask({ geometry: event.mapPoint } as any);
        const data = new RightClickData();
        data.clicked = raskPoint;
        data.hitlist = hitlist?.map(x => new GeometryInformation(ObjectUtils.deepCopy(x.geometry), { layer: x.layer }));
        this.showPopupOnGeometry(RightClickPopupComponent, raskPoint, data);
    }

    private setDrawLayerVisibility(layerid, visible) {
        const layer = this.mapsettings.drawLayers.find(x => x.id === layerid);
        const arcgislayer = this.getFeatureOrGraphicsArcgisLayer(layerid);
        if (!ObjectUtils.isNullOrUndefined(layer)) {
            layer.visible = visible;
            arcgislayer.visible = visible;
        }
    }

    private setFeatureLayerVisibility(layerid: string, visible: boolean): Observable<boolean> {
        const iMapLayer = this.mapsettings.featurelayers.find(x => x.id === layerid);
        const layerView = this.view.layerViews.find(x => x.layer.id === layerid);
        if (!iMapLayer || !layerView) {
            return undefined;
        }
        iMapLayer.visible = visible;
        layerView.layer.visible = visible;
        // set all sublayers visible, but dont hide them
        if (this.hasSublayerVisibility(layerView.layer)) {
            (layerView.layer as MapImageLayer).sublayers.forEach(sub => {
                sub.visible = visible;
            });
        }
        return from(reactiveUtils.whenOnce(() => !layerView.updating, this.abortController.signal));
    }

    private setMapSettingsDefault(property: any, value: any) {
        if (ObjectUtils.isNullOrUndefined(this.mapsettings[property]) || this.mapsettings[property].length === 0) {
            this.mapsettings[property] = value;
        }
    }

    private setMapSettingsDefaults() {
        this.setMapSettingsDefault("id", { id: "default" });
        this.setMapSettingsDefault("height", "100%");
        this.setMapSettingsDefault("mapVerticalStart", "92");
        this.setMapSettingsDefault("center", [
            17.9531,
            59.3565
        ]);
        this.setMapSettingsDefault("zoom", 6);
        this.setMapSettingsDefault("baselayers", [
            {
                id: "metriabakgrund",
                visible: true
            }
        ]);
        this.setMapSettingsDefault("featurelayers", [
            {
                id: "AdministrativaGranser",
                visible: true,
                showGeometriesMenu: true
            }
        ]);
    }

    private setMediaLayerVisibility(layerid: string, visible: boolean) {
        const arcgisMediaLayer = this.getArcgisMediaLayer(layerid);
        if (!ObjectUtils.isNullOrUndefined(arcgisMediaLayer)) {
            arcgisMediaLayer.visible = visible;
        }
    }

    private setPassiveFilter(layerid: string, expression: string, sublayerids: string[] = null) {
        const passiveLayer = this.mapsettings.featurelayers.find(x => x.id === layerid);
        if (ObjectUtils.isNullOrUndefined(passiveLayer)) {
            console.error(`setPassiveFilter: ${layerid} view or sublayers not found or not loaded.`);
        } else {
            passiveLayer.filter = {
                expression: expression,
                sublayerids: sublayerids
            };
        }
    }

    private setPersistedVisibility(arclayer: Layer) {
        if (arclayer.type === LayerType.mapImage) {
            const layer = arclayer as MapImageLayer;
            if (this.isLayerPersisted(layer)) {
                // hanteras av lagerväljaren, användaren sätter visibilitet
                if (this.hasSublayerVisibility(layer)) {
                    const visibleSublayerIds = this.persistService.getVisibleSublayerIds(arclayer.id, this.defaults);
                    const sublayers = layer.allSublayers;
                    sublayers.forEach(sl => {
                        sl.visible = visibleSublayerIds?.some(v => v === sl.id);
                        this.toggleRelatedLayers(sl.visible, sl);
                    });
                }

                layer.visible = this.persistService.isVisible(layer.id, this.defaults);
                layer.opacity = this.persistService.getLayerOpacity(layer.id, this.defaults, layer.opacity);
            } else {
                // hanteras ej av lagerhanteraren, sätt visibilitet enligt mapsettings
                const visible = this.mapsettings.featurelayers.find(fl => fl.id === layer.id)?.visible === true;
                layer.visible = visible;
            }
        } else if (arclayer.type === LayerType.wms) {
            const layer = arclayer as WMSLayer;
            if (this.isLayerPersisted(layer)) {
                const visibleSublayerIds = this.persistService.getVisibleSublayerIds(arclayer.id, this.defaults);
                const sublayers = layer.allSublayers;
                sublayers.forEach(sl => {
                    sl.visible = visibleSublayerIds?.some(v => v === sl.id);
                    this.toggleRelatedLayers(sl.visible, sl);
                });
                layer.visible = this.persistService.isVisible(layer.id, this.defaults);
                layer.opacity = this.persistService.getLayerOpacity(layer.id, this.defaults, layer.opacity);
            } else {
                // hanteras ej av lagerhanteraren, sätt visibilitet enligt mapsettings
                const visible = this.mapsettings.featurelayers.find(fl => fl.id === layer.id)?.visible === true;
                layer.visible = visible;
            }
        } else if (arclayer.type === LayerType.graphics) {
            const draw = this.getLayer(arclayer.id);
            if (draw?.hideInLayerList) {
                arclayer.visible = true;
            } else {
                arclayer.visible = this.persistService.isVisible(arclayer.id, this.defaults);
            }
        }
    }

    private setupSubComponents() {
        this.mapSubComponents()?.forEach(component => {
            component.preInit(this.mapsettings);
            this.widgetMenuButtons.push(...component.buttons);
            component.refresh.pipe(
                takeUntil(this.ngUnsubscribe$)
            ).subscribe(params => this.refreshGeometries(params.layerId));
            this.mapsettings.drawLayers.push(...component.drawLayers);
            component.onElementReady.subscribe((e) => {
                this.registerWidget(e, component.widgetId, true);
                e.remove();
            });
        });
    }

    private showVisibleGeometries(layerId: string) {
        const raskLayer = this.getLayer(layerId);
        const arcgisLayer = this.getArcgisLayer(layerId);

        let geometriesToDisplay: Graphic[];
        if (raskLayer.allowInvisibleGeometries) {
            geometriesToDisplay = this.arcgisGeometries.get(layerId);
            geometriesToDisplay.forEach(g => {
                const geo = raskLayer.geometries?.find(x => x.id === g.attributes.id);
                g.visible = geo.visible;
            });
        } else {
            // Optimerad framtagning av alla synliga geometrier
            const visibleRGGList = raskLayer.geometries?.filter(g => g.visible);
            const aGGList = this.arcgisGeometries.get(layerId);
            const visibleRGG = _.chain(visibleRGGList).keyBy("id").value();
            const visibleArcgisGraphics = (aGGList || []).filter(g => _.has(visibleRGG, g.attributes.id));
            geometriesToDisplay = visibleArcgisGraphics;
        }
        arcgisLayer.removeAll();
        arcgisLayer.addMany(geometriesToDisplay);
    }

    private signalAllLayersReady() {
        const event = new AllLayersReadyEvent(this.mapsettings.id, this.view.map.layers.toArray());
        this.eventService.emit(event);
    }

    private signalHoverIn(layer: IMapDrawLayer, geometry: IMapGeometry, mouse: IMapMouse) {
        if (layer?.onHoverIn) {
            layer.onHoverIn(geometry, mouse);
        }
        const event = new MapHoverEvent(layer?.id, geometry, mouse);
        this.eventService.emit(event);
    }

    private signalHoverOut(layer: IMapDrawLayer, geometry: IMapGeometry, mouse: IMapMouse) {
        if (layer?.onHoverOut) {
            layer.onHoverOut(geometry, mouse);
        }
        const event = new MapHoverEvent(layer?.id, geometry, mouse, false);
        this.eventService.emit(event);
    }

    private signalLayersPan() {
        this.mapsettings.drawLayers?.forEach(raskLayer => {
            if (raskLayer.onPan) {
                const graphics = this.getAllGeometriesInExtent(raskLayer);
                raskLayer.onPan(graphics);
            }
        });
    }

    private signalMouseEvent(mouse: any) {
        const mapClickedEvent = new MapClickedEvent(mouse.point, mouse.coords);
        this.eventService.emit(mapClickedEvent);
    }

    private toggleRelatedLayers(visible: boolean, subLayer: Sublayer | WMSSublayer) {
        // If sublayer, make sure all parents are visible
        if (visible) {
            this.ensureVisible(subLayer);
        }
    }

    private updateHighlights(newHitlist: IHitItem[] = null) {
        if (newHitlist?.length <= 0 && this.lastHoverObjects.length === 0) {
            return null;
        }

        const tobeHighlighted = differenceWith(
            newHitlist,
            this.lastHoverObjects,
            (arrVal, othVal) => arrVal.geometry.id === othVal.geometry.id && arrVal.layer.id === othVal.layer.id);
        const wasHighlighted = differenceWith(
            this.lastHoverObjects,
            newHitlist,
            (arrVal, othVal) => arrVal.geometry.id === othVal.geometry.id && arrVal.layer.id === othVal.layer.id);

        if (wasHighlighted.length > 0) {
            this.clearAllHighlights();
            wasHighlighted.forEach(hitItem => this.lastHoverObjects.splice(this.lastHoverObjects.indexOf(hitItem), 1));
        }
        tobeHighlighted?.forEach(hitItem => {
            if (this.lastHoverObjects.length < 1) {
                this.lastHoverObjects.push(hitItem);
            }
        });
        this.lastHoverObjects.forEach(hl => {
            if (hl.layer.onHoverIn || hl.layer.onClick) {
                this.highlightGraphics(hl.layer.id, [hl.graphic]);
            }
        });
        return {
            in: this.lastHoverObjects,
            out: wasHighlighted
        };
    }
}
